import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { KeyRound, Search, Send } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { EmptyState, ErrorState, LoadingState, Notice, PageHeader, Spinner } from "@/components/common/States";
import { PasswordForm, ProfileCard } from "@/components/settings/AccountSettings";
import { ResetLinkDialog } from "@/components/settings/ResetLinkDialog";
import { useAuth } from "@/context/AuthContext";
import { api, errorMessage, fetcher } from "@/lib/api";
import { ROLE_LABELS } from "@/lib/labels";

const JENIS = {
  reseller: { label: "Reseller", tone: "bg-blue-50 text-blue-800 border-blue-200 dark:bg-blue-950/50 dark:text-blue-200 dark:border-blue-900" },
  sekolah: { label: "Akun sekolah", tone: "bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700" },
};

const AccountsPanel = () => {
  const [cari, setCari] = useState("");
  const [jenis, setJenis] = useState("semua");
  const [hasil, setHasil] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const q = useQuery({ queryKey: ["platform-accounts"], queryFn: () => fetcher("/platform/accounts") });

  const akun = useMemo(() => {
    const n = cari.trim().toLowerCase();
    return (q.data || []).filter((a) =>
      (jenis === "semua" || a.kind === jenis) &&
      (!n || [a.full_name, a.email, a.school_name, a.label].some((v) => (v || "").toLowerCase().includes(n))));
  }, [q.data, cari, jenis]);

  const kirim = async (a) => {
    setBusyId(a.user_id);
    try {
      const { data } = await api.post(`/platform/accounts/${a.user_id}/reset-password`);
      setHasil({ ...data, nama: a.full_name || a.email });
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <section className="space-y-4" data-testid="platform-accounts-panel">
      <Notice tone="info">
        Tautan reset dikirim ke email akun reseller atau anggota sekolah. Bila email otomatis belum aktif,
        tautan tetap tampil di layar untuk disalin dan dikirim lewat WhatsApp.
      </Notice>
      <div className="flex flex-col gap-3 md:flex-row md:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={cari} onChange={(e) => setCari(e.target.value)} className="pl-9"
            aria-label="Cari akun" placeholder="Cari nama, email, atau sekolah…" data-testid="accounts-search" />
        </div>
        <Select value={jenis} onValueChange={setJenis}>
          <SelectTrigger className="md:w-48" aria-label="Jenis akun" data-testid="accounts-kind-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="semua">Semua akun</SelectItem>
            <SelectItem value="reseller">Akun reseller</SelectItem>
            <SelectItem value="sekolah">Akun sekolah</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {q.isLoading ? <LoadingState rows={4} /> : q.error ? <ErrorState error={q.error} onRetry={q.refetch} /> : !akun.length ? (
        <EmptyState title="Tidak ada akun yang cocok" description="Ubah kata kunci atau filter jenis akun." />
      ) : (
        <>
          <p className="text-sm text-muted-foreground" data-testid="accounts-count">
            Menampilkan {akun.length} dari {(q.data || []).length} akun
          </p>
          <ul className="divide-y rounded-xl border" data-testid="accounts-list">
            {akun.map((a) => {
              const j = JENIS[a.kind];
              return (
                <li key={a.user_id} className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between"
                  data-testid={`account-row-${a.user_id}`}>
                  <div className="min-w-0">
                    <p className="font-semibold">{a.full_name || a.email}</p>
                    <p className="text-xs text-muted-foreground break-all">{a.email}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {a.kind === "reseller" ? `Reseller · ${a.label}` : `${a.label}${a.role ? ` · ${ROLE_LABELS[a.role] || a.role}` : ""}`}
                      {a.schools_count > 1 ? ` · ${a.schools_count} sekolah` : ""}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${j.tone}`}>
                      {j.label}
                    </span>
                    <Button size="sm" variant="outline" onClick={() => kirim(a)} disabled={busyId === a.user_id}
                      data-testid={`account-reset-${a.user_id}`}>
                      {busyId === a.user_id ? <Spinner /> : <Send className="h-4 w-4 mr-1.5" />} Kirim tautan reset
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        </>
      )}
      <ResetLinkDialog hasil={hasil} onClose={() => setHasil(null)} />
    </section>
  );
};

export default function PlatformSettings() {
  const { me } = useAuth();
  const profil = [
    { label: "Nama", value: me.user.full_name },
    { label: "Peran", value: "Admin Platform (Founder)" },
    { label: "Email akun (login)", value: me.user.email, mono: true },
  ];
  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Pusat Kendali" title="Pengaturan"
        description="Profil akun founder, pengaturan kata sandi, dan pengiriman tautan reset untuk akun reseller serta anggota sekolah." />
      <Tabs defaultValue="akun" data-testid="platform-settings-tabs">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="akun" data-testid="settings-tab-profile">Profil</TabsTrigger>
          <TabsTrigger value="sandi" data-testid="settings-tab-password">Reset Kata Sandi</TabsTrigger>
          <TabsTrigger value="reset" data-testid="settings-tab-reset">
            <KeyRound className="h-3.5 w-3.5 mr-1.5" /> Tautan Reset Akun
          </TabsTrigger>
        </TabsList>
        <TabsContent value="akun" className="mt-4"><ProfileCard fields={profil} /></TabsContent>
        <TabsContent value="sandi" className="mt-4"><PasswordForm /></TabsContent>
        <TabsContent value="reset" className="mt-4"><AccountsPanel /></TabsContent>
      </Tabs>
    </div>
  );
}
